import { ApolloClient, ApolloError, createHttpLink, FetchResult, gql, HttpOptions, InMemoryCache, Observable } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { ErrorLink, onError } from '@apollo/client/link/error';
import { GraphQLError } from 'graphql';
import { getOptionalAuthHeader, UNAUTHORIZED_MESSAGES } from '../../auth/utils';
import { AuthenticationError, HttpError, HttpResponseParsingError, InvalidUrlError } from '../errors/errors';
import { CursorInfo } from '../types/pagination';
import { GQLIdEncoder } from '../utils/GQLIdEncoder';

/**
 * This is required for types that have Subtypes (where one type implements another type) so that we can
 * query all the types from the base type.
 * There is a way to generate this possibleTypes object automatically. However, it requires us to
 * perform an axios request to introspect the schema which is problematic for the way we currently setup the client.
 * I think it would be worth it to rethink the setup of the client and BaseGQLService to be able to
 * add the schema introspection.
 */
export const POSSIBLE_TYPES: { [key: string]: string[] } = {
  Report: ['Report', 'ReportPending', 'ReportFailed', 'ReportDone'],
  Trace: ['Trace', 'TracePending', 'TraceFailed', 'TraceDone'],
};

// https://developer.mozilla.org/en-US/docs/Web/API/DOMException#aborterror
const CANCELED_NETWORK_ERRORS = ['ERR_CANCELED', 'AbortError'];

/**
 * Normalize the different type of errors that can be returned for a GraphQL query
 * and logout the user in case of authentication errors
 */
export const ERROR_LINK: ErrorLink.ErrorHandler = ({ networkError, graphQLErrors, response: { errors } = { errors: [], data: null } }) => {
  const responseErrors: string[] = [];
  const applicationErrors: string[] = [];
  if (networkError) {
    if (CANCELED_NETWORK_ERRORS.includes(networkError.name)) {
      return Observable.of({ errors: [new GraphQLError('cancelAllRequests')] });
    }
    const nestedErrors = (networkError as any)?.result?.errors;
    if (nestedErrors) {
      responseErrors.push(...(nestedErrors || []).map((e: any) => e.message || e));
    } else {
      responseErrors.push(networkError.message);
    }
  }
  if (graphQLErrors?.length) {
    responseErrors.push(...graphQLErrors.map((e) => e.message || e.toString()));
  }
  if (errors?.length) {
    applicationErrors.push(...errors.map((e) => e.message));
  }
  const isBadApiKey = [...responseErrors, ...applicationErrors].find((e) => UNAUTHORIZED_MESSAGES.includes(e));
  if (isBadApiKey) {
    // Force redirection to log out to push user out of the app
    return Observable.of({ errors: [new GraphQLError(isBadApiKey, { originalError: new AuthenticationError() })] });
  }
  if (applicationErrors.length) {
    return Observable.of({ errors: applicationErrors.map((e) => new GraphQLError(e, { originalError: new HttpError(e) })) });
  }
  return Observable.of({ errors: responseErrors.map((e) => new GraphQLError(e)) });
};

export class BaseGQLService {
  private client = this.getClient();
  private abortController: AbortController | null = null;
  protected readonly gqlIdEncoder = new GQLIdEncoder();

  /**
   * By default, `ApolloClient` used with `InMemoryCache` explicitly
   * adds `__typename` to query nodes to maintain its cache.
   * This can increase the size of the response significantly: repositories
   * can override this method to prevent this after careful evaluation that
   * they do not need it.
   */
  protected addTypename() {
    return true;
  }

  cancelAllRequests() {
    if (this.abortController !== null) {
      this.abortController.abort({ message: 'cancelAllRequests' });
    }

    this.client = this.getClient();
  }

  getClient(): ApolloClient<any> {
    /**
     * We leverage the existing support for the AbortController through the ApolloClient.
     * AbortSignal is attached to the requests that are triggered and we can abort any requests in progress through the AbortController.
     *
     * AbortController and AbortSignal aren't supported in some browsers, but in the majority of cases it will work ok.
     *
     * More details on AbortController: https://developer.mozilla.org/en-US/docs/Web/API/AbortController
     */
    const linkOptions: HttpOptions = { uri: '/api/v2' };

    // If we have a support for AbortController, use it and add the signal to the linkOptions
    if (typeof AbortController !== 'undefined') {
      this.abortController = new AbortController();

      linkOptions.fetchOptions = {
        signal: (this.abortController as AbortController).signal,
      };
    }

    const authLink = setContext((_, { headers }) => ({
      headers: {
        ...headers,
        // Do not call `getOptionalAuthHeader` outside this method: it would try to read it as soon as any
        // service that extends BaseGQLService is created, likely causing the app to start
        // looping on a login-logout flow
        ...getOptionalAuthHeader(),
      },
    }));
    const httpLink = createHttpLink(linkOptions);
    const errorLink = onError(ERROR_LINK);

    return new ApolloClient({
      defaultOptions: { query: { fetchPolicy: 'no-cache' } },
      link: authLink.concat(errorLink).concat(httpLink),
      cache: new InMemoryCache({ possibleTypes: POSSIBLE_TYPES, addTypename: this.addTypename() }),
    });
  }

  async query<T = any>(query: string, variables: Record<string, any> = {}, mapper: (data: any) => T = (d) => d): Promise<T> {
    return new Promise<T>((res, rej) => {
      // Run the query
      this.client
        .query<T>({
          variables,
          query: gql`
            ${query}
          `,
        })
        .then(({ data }) => {
          // All good, map the response and resolve the promise
          res(mapper(data));
        })
        .catch((e: any) => {
          if (this.isAbortSignalError(e)) {
            // Don't resolve the promise to avoid further error handling.
            // This is a cancelled request, we can leave it "hanging" for the garbage collector to collect.
            // The actual request is cancelled and we won't cause any further state changes.
            return;
          }

          // Something went wrong, reject the main promise with a mapped error
          rej(this.throwError(e));
        });
    });
  }

  private isAbortSignalError(e: any) {
    return e instanceof ApolloError && e.message === 'cancelAllRequests';
  }

  async mutate<T = FetchResult<any>>(query: string, variables: Record<string, any> = {}, mapper: (data: any) => T = (d) => d): Promise<T> {
    return new Promise<T>((res, rej) => {
      // Run the mutation
      this.client
        .mutate<T>({
          variables,
          mutation: gql`
            ${query}
          `,
        })
        .then(({ data }) => {
          // All good, map the response and resolve the promise
          res(mapper(data));
        })
        .catch((e: any) => {
          if (this.isAbortSignalError(e)) {
            // Don't resolve the promise to avoid further error handling.
            // This is a cancelled request, we can leave it "hanging" for the garbage collector to collect.
            // The actual request is cancelled and we won't cause any further state changes.
            return;
          }

          // Something went wrong, reject the main promise with a mapped error
          rej(this.throwError(e));
        });
    });
  }

  private throwError(e: any) {
    if (!(e instanceof ApolloError) && !(e instanceof GraphQLError) && !(e instanceof InvalidUrlError)) {
      return new HttpResponseParsingError(e.message);
    }
    let originalError: Error | null = null;
    if (e instanceof ApolloError) {
      originalError = (e?.graphQLErrors?.[0] as GraphQLError)?.originalError || e.networkError || null;
    }
    if (e instanceof GraphQLError) {
      originalError = e?.originalError || null;
    }
    if (originalError instanceof AuthenticationError || originalError instanceof HttpError || originalError instanceof InvalidUrlError) {
      return originalError;
    }
    return e;
  }

  protected mapCursorInfo(cursor: CursorInfo) {
    return {
      after: null,
      before: null,
      first: null,
      last: null,
      ...cursor,
    };
  }
}
